/**
 * LPAAS 发布自动化 —— 纯函数层
 *
 * 全部是无副作用/无 JSX 的函数（除 URL 拼装那两个只读 window），
 * 目的是让「最容易出错的四处机制」里的判定部分能直接被单测覆盖（方案 §9.1）。
 *
 * 依赖方向：utils → constants（🔴 不许 import services，否则与 services 形成循环）。
 */
import {
  isSuccessCode,
  ERR,
  ERR_DEPRECATED,
  POLL,
  TAG,
  ROUTE,
  APP_BASE_FALLBACK,
  QUERY_KEY,
  BATCH_STATUS,
  ACTIVE_SERVICE_STATUS,
  SERVICE_TAG_COLOR,
  BATCH_TAG_COLOR,
  ITERATION_TAG_COLOR,
  BLOCK_REASON_TIP,
  BLOCK_REASON_UNKNOWN_TIP,
} from './constants';
import moment from 'moment';

/* -------------------------------------------------------------------------- */
/* 时间（列表页/详情页共用一套，避免两页对同一种时间戳格式化出两种结果）               */
/* -------------------------------------------------------------------------- */

/**
 * 时间 → 毫秒；不可解析返回 null（🔴 不返回 NaN，让调用方一眼看出「没有值」）。
 * 兼容三种后端出参：① 格式化串 `yyyy-MM-dd HH:mm:ss`（Safari 不认空格分隔 → 换 T 再解析）；
 * ② **epoch 毫秒**（Jackson 默认把 {@code java.util.Date} 序列化成数字，详情页的
 *   {@code startTime/endTime} 就是这一类，不兼容就会让「耗时」永远展「-」）；
 * ③ 纯数字串（字符串形式的 epoch 毫秒）。
 * 注意 mock 里阶段时间是 `15:20:31` 这种只有时分的串，moment 会按**今天**补日期，相对耗时尚可对。
 */
export function toTimeMs(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return isFinite(v) ? v : null;
  const str = String(v).trim();
  // 纯数字串：10 位是秒、13 位是毫秒（两者都归一到毫秒）
  if (/^\d{10}$/.test(str)) return Number(str) * 1000;
  if (/^\d{13}$/.test(str)) return Number(str);
  const m = moment(str.replace(' ', 'T'));
  return m.isValid() ? m.valueOf() : null;
}

/** 时间串 → `YYYY-MM-DD HH:mm`；解析不了就原样返回（不吞掉后端给的怪值） */
export function formatTime(v, fmt = 'YYYY-MM-DD HH:mm') {
  const ms = toTimeMs(v);
  return ms === null ? dash(v) : moment(ms).format(fmt);
}

/**
 * 毫秒 → 「2 小时 5 分」「1 分 20 秒」「45 秒」。
 * 🔴 只是个展示函数：算不出真实耗时（缺时间、跨天、负值）时由调用方决定展「-」，这里不编数字。
 */
export function formatDuration(ms) {
  if (ms === null || ms === undefined || !isFinite(ms) || ms < 0)
    return dash(null);
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h) return `${h} 小时 ${m} 分`;
  if (m) return `${m} 分 ${sec} 秒`;
  return `${sec} 秒`;
}

/* -------------------------------------------------------------------------- */
/* 空值与不可信输入                                                             */
/* -------------------------------------------------------------------------- */

/** 空值兜底：null / undefined / '' → 「-」，🔴 页面上不许出现 undefined 字面量（AC-06） */
export function dash(v, fallback = '-') {
  return v === null || v === undefined || v === '' ? fallback : v;
}

/**
 * 剥 HTML 标签（不翻译、不改语义、不做白名单解析）。
 *
 * 为什么必须做：failReason / lpaasFailReason / 配置 remark / 报告 unmatchedApps
 * 都是从 LPaaS `message`（HTML 富文本，如 `<font color=red>xx</font>`）透传来的
 * **不可信输入**。全站禁止 dangerouslySetInnerHTML（对接文档 §10-1），
 * 这里只剥标签、保留文字，React 默认转义再兜一层。
 */
export function stripHtmlSafe(input) {
  if (input === null || input === undefined) return '';
  return String(input)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .trim();
}

/** 阶段/状态 → Tag 类名（未知值一律落 default，不许自造第六种颜色） */
export function serviceTagClass(status) {
  return SERVICE_TAG_COLOR[status] || SERVICE_TAG_COLOR.default;
}

/** 批次状态 → Tag 类名（TIMEOUT 按失败口径，走 BATCH_TAG_COLOR 而不是迭代表） */
export function batchTagClass(status) {
  return BATCH_TAG_COLOR[status] || BATCH_TAG_COLOR.default;
}

export function iterationTagClass(status) {
  return ITERATION_TAG_COLOR[status] || ITERATION_TAG_COLOR.default;
}

export { TAG };

/* -------------------------------------------------------------------------- */
/* URL（详情页地址拼接；🔴 2026-10 产品口径：所有入口都不再新开页签，          */
/*      原窗口绝对地址的 appUrl/detailUrl/openDetailTab 已删，只留路由内路径）   */
/* -------------------------------------------------------------------------- */

/** 应用前缀：umi3 注入的 window.routerBase（= .umirc base），取不到兜 '/litta-palmer' */
export function appBase() {
  const base =
    typeof window !== 'undefined' && window.routerBase
      ? String(window.routerBase)
      : APP_BASE_FALLBACK;
  return base.replace(/\/+$/, '');
}

/** 拼站内绝对地址：appUrl(ROUTE.DETAIL, { iterationId: 12 }) */
export function appUrl(path, query) {
  const qs = Object.entries(query || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${appBase()}${path}${qs ? `?${qs}` : ''}`;
}

/**
 * 详情页 **路由内路径**（不带应用前缀）：detailPath(12) → `/technical/lpaasPublish/detail?iterationId=12`。
 * 🔴 给 `history.push` 用（列表【查看详情】、发布成功跳详情都走它）—— umi 的 history 内部会
 *    自己补 routerBase 前缀，传绝对地址会变成 `/litta-palmer/litta-palmer/...` 双前缀 →
 *    路由匹配不到 → 掉进权限兕底页。
 */
export function detailPath(iterationId, anchorBatchNo) {
  const qs = Object.entries({
    [QUERY_KEY.ITERATION_ID]: iterationId,
    [QUERY_KEY.ANCHOR_BATCH_NO]: anchorBatchNo,
  })
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${ROUTE.DETAIL}${qs ? `?${qs}` : ''}`;
}

/* -------------------------------------------------------------------------- */
/* 轮询判定（D2 的病根：isActive / intervalMs 必须由「数据」派生，不由 state 初值）  */
/* -------------------------------------------------------------------------- */

/** 任一服务还在「非终态」集合里 */
export function hasActiveService(services) {
  return (services || []).some((s) => ACTIVE_SERVICE_STATUS.includes(s.status));
}

/**
 * URL 协议白名单：只放行 http/https（允许相对/根路径，交给当前 origin）。
 * 🔴 后端下发的报告链接、LPaaS 控制台地址、扫码授权链接都会被直接渲染成
 *    `<a href>` / `window.open` —— 不加白名单，存储型 `javascript:` 链接
 *    就是存储型 XSS（用户一点，凭证域脚本跑起来）。不入白名单的值一律降级纯文本/隐藏。
 * @param {?string} url
 * @return {boolean}
 */
export function isSafeHttpUrl(url) {
  const s = String(url ?? '').trim();
  if (!s) return false;
  if (s.startsWith('/') && !s.startsWith('//')) return true; // 站内相对链接
  return /^https?:\/\//i.test(s);
}

/**
 * 迭代下是否还有活跃批次。
 * 🔴 不能用迭代状态判：迭代 SUCCESS 时追加的批次仍可能在跑（D12 / AC-07 反面）。
 */
export function hasActiveBatch(detail) {
  return (detail?.batches || []).some(
    (b) => b.status === BATCH_STATUS.PUBLISHING,
  );
}

/**
 * 选轮询间隔：还有未收口批次 → 5s；全部收口 → 0（不轮）。
 *
 * 🔴 2026-10-08 起**只有一个档**（《方案存档-LPAAS发布轮询改造（前端驱动+分钟级兜底）》§4.1）：
 *    原「有执行中 5s / 只有排队 15s」两档已废 —— 后端双频 Job 合成 60s 兜底后，
 *    详情页轮询是「秒级给人看」的**唯一来源**，排队的服务也得在 5s 内看到它跳到执行中。
 * 🔴 判据是**批次状态**（`hasActiveBatch`），不是服务状态：
 *    服务状态集合漏一个（历史上漏过 QUEUED / RUNNING / UNKNOWN_OUTCOME）就会把「还有服务在跑」
 *    判成「全终态」→ 间隔 0 → 页面看起来卡住不动。改判批次后这个坑结构性消失，
 *    后端以后新增服务状态也不用再回头改前端节奏。
 * 🔴 只服务**详情页**：列表页按产品口径不做定时刷新（要更新就点【刷新】）。
 * @return {number} 0 = 不轮询
 */ export function pickPollInterval(detail, poll = POLL) {
  return hasActiveBatch(detail) ? poll.ACTIVE_MS : 0;
}

/* -------------------------------------------------------------------------- */
/* 依赖：环检测 / 悬空检测 / 自动补上游                                          */
/* -------------------------------------------------------------------------- */

/**
 * 依赖成环检测（DFS 三色标记）。
 *
 * 用途有两个：① 弹窗里选依赖时即时提示（后端 07020039 仍会兜底重判）；
 * ② 提交前把成环的服务定位出来做高亮。
 * 🔴 只做即时提示，不做「前端判完就放行」——服务端才是权威（对接文档 §5.2）。
 *
 * @param {Array<{appName:string, dependsOn?:string[]}>} items
 * @return {string[]} 环上的服务名（无环返回 []）
 */
export function detectCycle(items) {
  const graph = new Map();
  (items || []).forEach((it) => {
    graph.set(it.appName, (it.dependsOn || []).filter(Boolean));
  });
  const DONE = 2;
  const STACK = 1;
  const color = new Map();
  const cycle = [];

  const walk = (name, stack) => {
    color.set(name, STACK);
    stack.push(name);
    for (const dep of graph.get(name) || []) {
      if (!graph.has(dep)) continue; // 依赖不在本次清单里 = 悬空，交 findDanglingDeps 管
      const c = color.get(dep);
      if (c === STACK) {
        // 找到回边：把环上那一段截出来（从 dep 开始回到 dep）
        const from = stack.indexOf(dep);
        cycle.push(...stack.slice(from), dep);
        return true;
      }
      if (c === DONE) continue;
      if (walk(dep, stack)) return true;
    }
    stack.pop();
    color.set(name, DONE);
    return false;
  };

  for (const name of graph.keys()) {
    if (color.get(name) === DONE) continue;
    if (walk(name, [])) break; // 一期只定位第一个环，够用
  }
  // 自依赖 A→A 时上面会推出 [A, A]，去重后仍能定位到该服务
  return Array.from(new Set(cycle));
}

/**
 * 悬空依赖：勾了下游、上游没勾 → 黄条提示但**不拦提交**（07020046 是提示级）。
 * @return {Array<{appName:string, missing:string[]}>}
 */
export function findDanglingDeps(selected) {
  const picked = new Set((selected || []).map((s) => s.appName));
  return (selected || [])
    .map((s) => ({
      appName: s.appName,
      missing: (s.dependsOn || []).filter((d) => !picked.has(d)),
    }))
    .filter((row) => row.missing.length > 0);
}

/**
 * 体验联动（不是校验，后端不检查）：勾选下游时，把它依赖且尚未勾选的上游一起带上。
 * 🔴 反向不成立：取消上游 A 不许清掉下游 B，只把 B 的依赖标黄（方案 §6.2）。
 *
 * @param {string[]} pickedNames 当前已勾选
 * @param {Array<{appName:string, dependsOn?:string[]}>} apps 全量清单（只取本次新勾项的依赖）
 * @param {string[]} justSelected 本次新增勾选的服务名
 * @return {string[]} 需要追加勾选的上游服务名
 */
export function collectUpstreamToAdd(pickedNames, apps, justSelected) {
  const picked = new Set(pickedNames || []);
  const index = new Map((apps || []).map((a) => [a.appName, a]));
  const add = [];
  const queue = (justSelected || []).slice();
  while (queue.length) {
    const name = queue.shift();
    const app = index.get(name);
    (app?.dependsOn || []).forEach((dep) => {
      const depApp = index.get(dep);
      // 只在「清单里存在 + 可选」时自动补，不可选的上游交给用户自己决定
      if (!depApp || !depApp.selectable) return;
      if (picked.has(dep)) return;
      picked.add(dep);
      add.push(dep);
      queue.push(dep); // 上游还有上游，递归补齐
    });
  }
  return add;
}

/**
 * 清单搜索：名称/描述模糊匹配，空格分词取 AND。
 * （「litta user」要同时命中名称里的 litta 与 user，PRD 的可搜要求）
 */
export function matchKeyword(app, keyword) {
  const kw = (keyword || '').trim().toLowerCase();
  if (!kw) return true;
  const hay = `${app.appName || ''} ${app.appDesc || ''}`.toLowerCase();
  return kw.split(/\s+/).every((token) => hay.includes(token));
}

/* -------------------------------------------------------------------------- */
/* 错误码 → 动作分发中心                                                        */
/* -------------------------------------------------------------------------- */

/**
 * 写操作回包是不是「生产缺/失效二次确认」（G3）—— `07020041`。
 *
 * 🔴 这个码**一码两义**：master 缺 confirmToken（要弹确认框）／sit 分支与环境不一致（要提示改分支）。
 *    所以它只能当「去 `/confirm/issue` 要个说法的信号」，最终弹不弹确认框看后端回的 `prodEnv`
 *    （见 hooks/useProdConfirm.js）。
 */
export const isProdConfirmRequired = (res) =>
  `${(res && res.code) || ''}` === ERR.ENV_OR_CONFIRM;

/**
 * 对接文档 §8 的唯一落地点：所有页面只消费这里返回的动作描述，
 * 全站只有这一个地方写 `if (code === ...)`，改口径只改一处，且能被单测覆盖。
 *
 * @param {Object} res 服务层出参 { code, msg, data }
 * @param {Object} ctx 上下文：{ branch } —— 07020041 一码两义靠它分流
 * @return {null|{type:'toast'|'warn'|'reauth'|'reconfirm'|'refresh'|'highlight',
 *                 msg:string, target?:string, keepRequestId?:boolean}}
 *         null = 成功，什么都不用做
 */
export function toUiAction(res, ctx = {}) {
  const code = `${(res && res.code) || ''}`;
  const msg = (res && res.msg) || '操作失败';
  // 🔴 成功码不止 '00000'，历史网关也回 '0'，一律走 isSuccessCode，别只比字面量
  if (isSuccessCode(code)) return null;

  // 作废码：连的是旧后端 / 跑的是旧前端，必须显眼，不许静默当普通失败
  if (ERR_DEPRECATED[code]) {
    return {
      type: 'toast',
      msg: `${msg}（该错误码对应逻辑已作废：${ERR_DEPRECATED[code]}，请核对前后端版本）`,
    };
  }

  switch (code) {
    // 🔴 「没授权」不得渲染成「发布失败」，走扫码闭环
    case ERR.AUTH_INVALID:
    case ERR.AUTH_IN_PROGRESS:
    case ERR.LPAAS_APP_NOT_FOUND:
      return { type: 'reauth', msg };

    // 🔴 一码两义：master = 缺二次确认；sit = 分支与环境不一致（按 branch 分流）
    case ERR.ENV_OR_CONFIRM:
      return ctx.branch === 'master'
        ? { type: 'reconfirm', msg: '本次为生产发布，请完成二次确认' }
        : {
            type: 'toast',
            msg: `目标环境与所选分支不一致：请检查分支或去掉环境不匹配的应用（${msg}）`,
          };

    case ERR.CYCLE:
      return { type: 'highlight', msg, target: 'cycle' };
    case ERR.SERVICES_EMPTY:
      return { type: 'highlight', msg, target: 'services' };
    case ERR.ITERATION_NO_INVALID:
      return { type: 'highlight', msg, target: 'iterationNo' };
    case ERR.BRANCH_EMPTY:
      return { type: 'highlight', msg, target: 'branch' };

    // 弹窗不关，让用户回清单调整
    case ERR.NOT_WHITELIST:
    case ERR.WEB_TYPE:
      return { type: 'toast', msg };

    case ERR.DEPENDENCY_MISSING:
      return { type: 'warn', msg }; // ✅ 提示不拦，可继续提交

    // 🔴 LPaaS 原文原样展示，不翻译不改写；本地状态一律不动
    case ERR.LPAAS_FAIL:
      return { type: 'toast', msg };

    // 提示 + 不换 ID、不重发（换 ID 会把「正在处理的首请求」变成两个批次）
    case ERR.DUPLICATE_REQUEST:
      return { type: 'toast', msg, keepRequestId: true };

    // 让服务端重算 canStop/canRepublish/canRetry，不要只 toast
    case ERR.NO_NEED_STOP:
    case ERR.STATE_NOT_ALLOW:
    case ERR.REPUBLISH_NOT_ALLOW:
      return { type: 'refresh', msg };

    default:
      return { type: 'toast', msg }; // 原样 toast，弹窗保持不关
  }
}

/** SKIPPED 行的 tooltip（字典外的值不猜语义，兜「未提供跳过原因」） */
export function blockReasonTip(blockReason) {
  if (!blockReason) return BLOCK_REASON_UNKNOWN_TIP;
  return BLOCK_REASON_TIP[blockReason] || `跳过原因：${blockReason}`;
}
